package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class RegisterRequest(
    @SerialName("display_name") val displayName: String,
    @SerialName("username") val username: String,
    @SerialName("email") val email: String,
    @SerialName("password") val password: String,
    @SerialName("accepted_terms_version") val acceptedTermsVersion: String? = null,
    @SerialName("accepted_privacy_version") val acceptedPrivacyVersion: String? = null,
    @SerialName("age_confirmed") val ageConfirmed: Boolean = false,
)

/** `identifier` accepts either an email address or a username. */
@Serializable
data class LoginRequest(
    @SerialName("identifier") val identifier: String,
    @SerialName("password") val password: String,
)

/**
 * Note these are TTLs in SECONDS, not absolute timestamps -- `access_expires_in: 900`,
 * `refresh_expires_in: 2592000`. The client computes its own deadline on receipt, which
 * also sidesteps client clock skew.
 */
@Serializable
data class TokenPair(
    @SerialName("access_token") val accessToken: String,
    @SerialName("refresh_token") val refreshToken: String,
    @SerialName("access_expires_in") val accessExpiresIn: Long,
    @SerialName("refresh_expires_in") val refreshExpiresIn: Long,
)

@Serializable
data class AuthSessionResponse(
    @SerialName("user") val user: UserPrivate,
    @SerialName("tokens") val tokens: TokenPair,
)

@Serializable
data class RefreshRequest(
    @SerialName("refresh_token") val refreshToken: String,
)

@Serializable
data class LogoutRequest(
    @SerialName("refresh_token") val refreshToken: String,
)

@Serializable
data class VerifyEmailRequest(
    @SerialName("token") val token: String,
)

@Serializable
data class ForgotPasswordRequest(
    @SerialName("email") val email: String,
)

@Serializable
data class ResetPasswordRequest(
    @SerialName("token") val token: String,
    /** The contract names this `new_password`, not `password`. */
    @SerialName("new_password") val newPassword: String,
)

@Serializable
data class ChangePasswordRequest(
    @SerialName("current_password") val currentPassword: String,
    @SerialName("new_password") val newPassword: String,
)

/** An active login. Sessions can be listed and revoked individually or all-but-current. */
@Serializable
data class Session(
    @SerialName("id") val id: Uuid,
    @SerialName("platform") val platform: String,
    @SerialName("device_name") val deviceName: String? = null,
    @SerialName("app_version") val appVersion: String? = null,
    @SerialName("ip_address") val ipAddress: String? = null,
    @SerialName("location_hint") val locationHint: String? = null,
    @SerialName("created_at") val createdAt: Timestamp,
    @SerialName("last_active_at") val lastActiveAt: Timestamp,
    @SerialName("is_current") val isCurrent: Boolean = false,
)

/** Short-lived Centrifugo connection token plus the channels this user may subscribe to. */
@Serializable
data class RealtimeTokenResponse(
    @SerialName("token") val token: String,
    @SerialName("expires_at") val expiresAt: Timestamp,
    @SerialName("channels") val channels: List<String> = emptyList(),
)
